import { useMemo } from 'react';
import { MultiSelectionToggleGroup } from 'src/components/common/MultiSelect/Toggle/MultiSelectionToggleGroup';
import { EventItemHeaderV2 } from 'src/components/Events/EventItemHeader/EventItemHeaderV2';
import {
  MetricsDisplay,
  MetricsDisplayPlaceholder,
} from 'src/components/Metrics';
import {
  BulletPoint,
  Metrics,
} from 'src/components/Metrics/MetricsDisplay/MetricsDisplay.styled';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { Content } from 'src/contexts/ContentContext';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { ContentId } from 'src/utils/constants/contentId';
import { getSalesSaleMetrics } from 'src/utils/ticketMetricUtils';
import { Feature, SaleMetrics } from 'src/WebApiController';

import {
  EventItemHeader,
  EventItemHeaderType,
  METRICS_PLACEHOLDER_DIMS,
} from '../../Events/EventItemHeader';
import * as styles from './EventSaleHeader.css';
import { FullPageEventAction } from './FullPageEventAction';

export const EventSaleHeader = (props: EventItemHeaderType) => {
  const { eventMetrics } = useCatalogMetricsContext<SaleMetrics>();
  const hasSalesTableV2Feature = useUserHasFeature(Feature.SalesTableV2);
  const hasUnfulfilledPercentFeature = useUserHasFeature(Feature.InventoryUnfulfilledPercentColumn);
  const hasPendingSourcingFeature = useUserHasFeature(Feature.InventoryPendingSourcingColumn);

  const hasConsignorMasterUI = useUserHasConsignorMasterUI();
  const { event, entityCount: salesCount, ...rest } = props;
  const metrics = useMemo(
    () => getSalesSaleMetrics(
      eventMetrics?.[event.virtId],
      undefined,
      hasUnfulfilledPercentFeature,
      hasPendingSourcingFeature
    ),
    [eventMetrics, event, hasUnfulfilledPercentFeature, hasPendingSourcingFeature]
  );
  const { performer, venue } = useGetEventFullInfo(event);

  const {
    eventsExpansion: { expandedListItems, toggleListItemExpansion },
  } = useCatalogDataContext();

  const onToggleGroup = (groupId: string) => {
    if (!expandedListItems.includes(groupId)) {
      toggleListItemExpansion([groupId]);
    }
  };

  return hasSalesTableV2Feature ? (
    <EventItemHeaderV2
      showYear={undefined}
      {...rest}
      event={event}
      performer={performer}
      venue={venue}
      actions={[
        <FullPageEventAction key="FullEventPageToggle" event={event} />,
        <MultiSelectionToggleGroup
          key="SalesEventSelectionGroupToggle"
          groupId={props.event.virtId}
          onToggle={onToggleGroup}
        />,
      ]}
      headerMetrics={
        !hasConsignorMasterUI &&
        metrics && (
          <MetricsDisplay
            key="headermetrics"
            mainMetrics={metrics.headerMetrics}
          />
        )
      }
      metrics={
        !hasConsignorMasterUI && (
          <div className={styles.eventSaleMetricsContainer}>
            {!metrics ? (
              <MetricsDisplayPlaceholder
                key="metrics-placeholder"
                {...METRICS_PLACEHOLDER_DIMS}
              />
            ) : (
              <MetricsDisplay
                key="metrics"
                mainMetrics={null}
                subMetrics={metrics.subMetrics}
              />
            )}
            <BulletPoint addBulletPoint />
            <Metrics className={styles.itemsCountDiv} key="items-count">
              {`${salesCount} `}
              <Content
                id={salesCount === 1 ? ContentId.Sale : ContentId.Sales}
              />
            </Metrics>
          </div>
        )
      }
      eventTags={metrics?.tags}
    />
  ) : (
    <EventItemHeader
      showYear={undefined}
      {...rest}
      event={event}
      performer={performer}
      venue={venue}
      actions={[
        <FullPageEventAction key="FullEventPageToggle" event={event} />,
        <MultiSelectionToggleGroup
          key="SalesEventSelectionGroupToggle"
          groupId={props.event.virtId}
          onToggle={onToggleGroup}
        />,
      ]}
      headerMetrics={
        !hasConsignorMasterUI &&
        metrics && (
          <MetricsDisplay
            key="headermetrics"
            mainMetrics={metrics.headerMetrics}
          />
        )
      }
      metrics={
        !hasConsignorMasterUI && (
          <div className={styles.eventSaleMetricsContainer}>
            {!metrics ? (
              <MetricsDisplayPlaceholder
                key="metrics-placeholder"
                {...METRICS_PLACEHOLDER_DIMS}
              />
            ) : (
              <MetricsDisplay
                key="metrics"
                mainMetrics={null}
                subMetrics={metrics.subMetrics}
              />
            )}
            <BulletPoint addBulletPoint />
            <div className={styles.itemsCountDiv} key="items-count">
              {`${salesCount} `}
              <Content
                id={salesCount === 1 ? ContentId.Sale : ContentId.Sales}
              />
            </div>
          </div>
        )
      }
      eventTags={metrics?.tags}
    />
  );
};
